import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, Wrench, Settings, Droplets, ChevronRight, Menu, X, AlertTriangle, CheckCircle, ArrowRight, Users, Award, Shield, Star, Target, Heart, Zap, ArrowUp, MessageCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Image URLs from user uploads
const IMAGES = {
  logo: "https://customer-assets.emergentagent.com/job_motor-pro-refresh/artifacts/h5mzv4ts_Professional%20logo%20design%20for%20JHWinders%20Enterprise.png",
  logoWhite: "https://customer-assets-lqy194kg.emergentagent.net/job_motor-pro-refresh/artifacts/f9to262x_JHWinders%20Logo%20-%20White.png",
  hero: "https://customer-assets.emergentagent.com/job_motor-pro-refresh/artifacts/eacazz5f_WhatsApp%20Image%202026-03-20%20at%2013.58.49.jpeg",
  electricMotorRepairs: "https://customer-assets-lqy194kg.emergentagent.net/job_motor-pro-refresh/artifacts/hzzhceax_7.%20Assemble%20Station.jpg",
  motorRewinding: "https://customer-assets-lqy194kg.emergentagent.net/job_motor-pro-refresh/artifacts/52nuqtek_6.%20Rewinding%20Sation.jpg",
  pumpRepairs: "https://customer-assets-lqy194kg.emergentagent.net/job_motor-pro-refresh/artifacts/jioqdfj5_9.1%20Pump%20Station.jpg",
  aboutWorkshop: "https://customer-assets-lqy194kg.emergentagent.net/job_motor-pro-refresh/artifacts/qr260qd1_5.%20Coil%20Station.jpg",
  // Services page images
  serviceElectricMotor: "https://customer-assets.emergentagent.com/job_motor-pro-refresh/artifacts/539104or_WhatsApp%20Image%202026-03-30%20at%2009.06.38.jpeg",
  serviceMotorRewinding: "https://customer-assets.emergentagent.com/job_motor-pro-refresh/artifacts/rvhjn7f9_WhatsApp%20Image%202026-03-28%20at%2016.54.45.jpeg",
  serviceRefurbishment: "https://customer-assets.emergentagent.com/job_motor-pro-refresh/artifacts/xm6scu7p_WhatsApp%20Image%202026-03-28%20at%2016.54.34%20%281%29.jpeg",
  serviceMaintenanceTesting: "https://customer-assets.emergentagent.com/job_motor-pro-refresh/artifacts/wjakp4xu_WhatsApp%20Image%202026-03-30%20at%2015.17.19.jpeg",
};

// Contact information
const CONTACTS = {
  daniel: { name: "Daniël", role: "Workshop Foreman", phone: "082 057 5452" },
  thinus: { name: "Thinus", role: "Sales Manager", phone: "076 423 3839" },
};

// Floating Buttons Component (Scroll to Top & WhatsApp)
const FloatingButtons = () => {
  const [showScrollTop, setShowScrollTop] = React.useState(false);

  React.useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const whatsappNumber = "27665484050"; // 066 548 4050 in international format
  const whatsappMessage = "Hello, I'm interested in your motor repair services.";
  const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`;

  return (
    <div className="floating-buttons" data-testid="floating-buttons">
      {/* WhatsApp Button */}
      <a 
        href={whatsappUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="whatsapp-btn"
        data-testid="whatsapp-btn"
        aria-label="Chat on WhatsApp"
      >
        <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </a>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button 
          onClick={scrollToTop}
          className="scroll-top-btn"
          data-testid="scroll-top-btn"
          aria-label="Scroll to top"
        >
          <ArrowUp size={24} />
        </button>
      )}
    </div>
  );
};

// Header Component
const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const location = useLocation();

  const navLinks = [
    { path: "/", label: "Home" },
    { path: "/about", label: "About" },
    { path: "/services", label: "Services" },
    { path: "/workshop", label: "Workshop" },
    { path: "/sales-team", label: "Sales Team" },
    { path: "/breakdown", label: "Breakdown" },
    { path: "/contact", label: "Contact" },
  ];

  return (
    <header className="header" data-testid="header">
      {/* Top Bar */}
      <div className="top-bar">
        <div className="top-bar-container">
          <div className="top-bar-content">
            <div className="contact-info">
              <a href={`tel:${CONTACTS.daniel.phone.replace(/\s/g, '')}`} className="contact-link" data-testid="header-phone-daniel">
                <Phone size={14} />
                <span>{CONTACTS.daniel.name}: {CONTACTS.daniel.phone}</span>
              </a>
              <a href={`tel:${CONTACTS.thinus.phone.replace(/\s/g, '')}`} className="contact-link" data-testid="header-phone-thinus">
                <Phone size={14} />
                <span>{CONTACTS.thinus.name}: {CONTACTS.thinus.phone}</span>
              </a>
            </div>
            <div className="emergency-badge" data-testid="emergency-badge">
              <AlertTriangle size={14} />
              <span>24/7 Emergency Service Available</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="main-nav">
        <div className="nav-container">
          <Link to="/" className="logo-link" data-testid="logo-link">
            <img src={IMAGES.logo} alt="JH Winders Enterprise PTY Ltd" className="logo" data-testid="logo" />
          </Link>

          {/* Desktop Navigation */}
          <div className="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`nav-link ${location.pathname === link.path ? 'active' : ''}`}
                data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
              >
                {link.label}
              </Link>
            ))}
            <a 
              href={`tel:${CONTACTS.daniel.phone.replace(/\s/g, '')}`} 
              className="cta-button"
              data-testid="header-cta"
            >
              <Phone size={18} />
              Call Now
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="mobile-menu-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="mobile-nav" data-testid="mobile-nav">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`mobile-nav-link ${location.pathname === link.path ? 'active' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.label}
              </Link>
            ))}
            <div className="mobile-contact">
              <a href={`tel:${CONTACTS.daniel.phone.replace(/\s/g, '')}`} className="mobile-phone">
                <Phone size={16} /> {CONTACTS.daniel.name}: {CONTACTS.daniel.phone}
              </a>
              <a href={`tel:${CONTACTS.thinus.phone.replace(/\s/g, '')}`} className="mobile-phone">
                <Phone size={16} /> {CONTACTS.thinus.name}: {CONTACTS.thinus.phone}
              </a>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

// Footer Component
const Footer = () => {
  const socialLinks = [
    {
      name: "Instagram",
      url: "https://www.instagram.com/jhwinders2017/",
      icon: (
        <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
        </svg>
      )
    },
    {
      name: "TikTok",
      url: "https://www.tiktok.com/@jhwinders",
      icon: (
        <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
          <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
        </svg>
      )
    },
    {
      name: "Facebook",
      url: "https://www.facebook.com/profile.php?id=61582021723957",
      icon: (
        <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      )
    }
  ];

  return (
    <footer className="footer" data-testid="footer">
      <div className="footer-container">
        <div className="footer-grid">
          {/* Company Info */}
          <div className="footer-section">
            <img src={IMAGES.logoWhite} alt="JH Winders Enterprise" className="footer-logo" />
            <p className="footer-description">
              Committed to Quality & Value. Your trusted partner for electric motor repairs, rewinding, and pump services.
            </p>
            {/* Social Media Links */}
            <div className="social-links" data-testid="social-links">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-link"
                  aria-label={social.name}
                  data-testid={`social-${social.name.toLowerCase()}`}
                >
                  {social.icon}
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div className="footer-section">
            <h4>Quick Links</h4>
            <Link to="/">Home</Link>
            <Link to="/about">About Us</Link>
            <Link to="/services">Our Services</Link>
            <Link to="/workshop">Workshop</Link>
            <Link to="/sales-team">Sales Team</Link>
            <Link to="/breakdown">Breakdown Service</Link>
            <Link to="/contact">Contact Us</Link>
          </div>

          {/* Services */}
          <div className="footer-section">
            <h4>Our Services</h4>
            <span>Electric Motor Repairs</span>
            <span>Motor Rewinding</span>
            <span>Pump Repairs</span>
            <span>Emergency Breakdown</span>
          </div>

          {/* Contact */}
          <div className="footer-section">
            <h4>Contact Us</h4>
            <div className="footer-contact">
              <Phone size={16} />
              <div>
                <p><strong>{CONTACTS.daniel.name}</strong> ({CONTACTS.daniel.role})</p>
                <a href={`tel:${CONTACTS.daniel.phone.replace(/\s/g, '')}`}>{CONTACTS.daniel.phone}</a>
              </div>
            </div>
            <div className="footer-contact">
              <Phone size={16} />
              <div>
                <p><strong>{CONTACTS.thinus.name}</strong> ({CONTACTS.thinus.role})</p>
                <a href={`tel:${CONTACTS.thinus.phone.replace(/\s/g, '')}`}>{CONTACTS.thinus.phone}</a>
              </div>
            </div>
          </div>
        </div>

        <div className="footer-bottom">
          <p>&copy; {new Date().getFullYear()} JH Winders Enterprise PTY Ltd. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

// Home Page
const Home = () => {
  const services = [
    {
      title: "Electric Motor Repairs",
      description: "Complete diagnostics and repair services for all types of electric motors. Our experienced technicians ensure your motors run efficiently.",
      image: IMAGES.electricMotorRepairs,
      icon: <Settings size={32} />,
    },
    {
      title: "Motor Rewinding",
      description: "Professional motor rewinding services using high-quality materials. We restore your motors to optimal performance.",
      image: IMAGES.motorRewinding,
      icon: <Wrench size={32} />,
    },
    {
      title: "Pump Repairs",
      description: "Expert pump repair and maintenance services. From submersible to centrifugal pumps, we handle it all.",
      image: IMAGES.pumpRepairs,
      icon: <Droplets size={32} />,
    },
  ];

  const stats = [
    { number: "25+", label: "Years Experience" },
    { number: "5000+", label: "Motors Repaired" },
    { number: "24/7", label: "Emergency Service" },
    { number: "100%", label: "Satisfaction" },
  ];

  return (
    <div className="home-page" data-testid="home-page">
      {/* Hero Section */}
      <section className="hero" data-testid="hero-section">
        <div className="hero-background">
          <img src={IMAGES.hero} alt="Motor Rewinding Workshop" data-testid="hero-image" />
          <div className="hero-overlay"></div>
        </div>
        <div className="hero-content">
          <h1 data-testid="hero-title">Expert Motor Rewinding & Repair Services</h1>
          <p data-testid="hero-subtitle">
            JH Winders Enterprise - Committed to Quality & Value. 
            Trusted by industries across South Africa for over 25 years.
          </p>
          <div className="hero-buttons">
            <Link to="/contact" className="btn-primary" data-testid="hero-cta-primary">
              Get a Quote <ArrowRight size={20} />
            </Link>
            <a href={`tel:${CONTACTS.daniel.phone.replace(/\s/g, '')}`} className="btn-secondary" data-testid="hero-cta-secondary">
              <Phone size={20} /> Call Now
            </a>
          </div>
          <div className="hero-contacts">
            <div className="hero-contact-card" data-testid="hero-contact-daniel">
              <Phone size={20} />
              <div>
                <span className="contact-name">{CONTACTS.daniel.name}</span>
                <span className="contact-role">{CONTACTS.daniel.role}</span>
                <a href={`tel:${CONTACTS.daniel.phone.replace(/\s/g, '')}`}>{CONTACTS.daniel.phone}</a>
              </div>
            </div>
            <div className="hero-contact-card" data-testid="hero-contact-thinus">
              <Phone size={20} />
              <div>
                <span className="contact-name">{CONTACTS.thinus.name}</span>
                <span className="contact-role">{CONTACTS.thinus.role}</span>
                <a href={`tel:${CONTACTS.thinus.phone.replace(/\s/g, '')}`}>{CONTACTS.thinus.phone}</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section" data-testid="stats-section">
        <div className="stats-container">
          {stats.map((stat, index) => (
            <div key={index} className="stat-item" data-testid={`stat-${index}`}>
              <span className="stat-number">{stat.number}</span>
              <span className="stat-label">{stat.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Services Section */}
      <section className="services-section" data-testid="services-section">
        <div className="section-container">
          <div className="section-header">
            <span className="section-tag">Our Expertise</span>
            <h2>Core Services</h2>
            <p>We provide comprehensive motor and pump services to keep your operations running smoothly.</p>
          </div>
          <div className="services-grid">
            {services.map((service, index) => (
              <div key={index} className="service-card" data-testid={`service-card-${index}`}>
                <div className="service-image">
                  <img src={service.image} alt={service.title} />
                  <div className="service-icon">{service.icon}</div>
                </div>
                <div className="service-content">
                  <h3>{service.title}</h3>
                  <p>{service.description}</p>
                  <Link to="/services" className="service-link">
                    Learn More <ChevronRight size={16} />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="why-choose-section" data-testid="why-choose-section">
        <div className="section-container">
          <div className="why-choose-grid">
            <div className="why-choose-content">
              <span className="section-tag">Why Choose Us</span>
              <h2>Trusted Expertise in Motor Services</h2>
              <p>With over 25 years of experience, JH Winders Enterprise has built a reputation for excellence in motor rewinding and repair services.</p>
              <div className="features-list">
                <div className="feature-item">
                  <CheckCircle size={24} />
                  <div>
                    <h4>Quality Workmanship</h4>
                    <p>All work performed to the highest industry standards</p>
                  </div>
                </div>
                <div className="feature-item">
                  <CheckCircle size={24} />
                  <div>
                    <h4>Fast Turnaround</h4>
                    <p>Minimize downtime with our efficient repair services</p>
                  </div>
                </div>
                <div className="feature-item">
                  <CheckCircle size={24} />
                  <div>
                    <h4>24/7 Emergency Support</h4>
                    <p>Round-the-clock assistance for urgent breakdowns</p>
                  </div>
                </div>
                <div className="feature-item">
                  <CheckCircle size={24} />
                  <div>
                    <h4>Competitive Pricing</h4>
                    <p>Quality service at fair, transparent prices</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="why-choose-image">
              <img src="https://customer-assets-lqy194kg.emergentagent.net/job_motor-pro-refresh/artifacts/yhq4x7dy_JHWinders-motor-rewinding-professional.jpg" alt="Motor Rewinding Professional" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section" data-testid="cta-section">
        <div className="cta-container">
          <div className="cta-content">
            <h2>Need Emergency Motor Repair?</h2>
            <p>Our team is available 24/7 for urgent breakdowns. Contact us now for immediate assistance.</p>
          </div>
          <div className="cta-buttons">
            <a href={`tel:${CONTACTS.daniel.phone.replace(/\s/g, '')}`} className="cta-primary" data-testid="cta-phone-daniel">
              <Phone size={20} />
              <div className="cta-btn-text">
                <span className="cta-btn-name">{CONTACTS.daniel.name}</span>
                <span className="cta-btn-number">{CONTACTS.daniel.phone}</span>
              </div>
            </a>
            <a href={`tel:${CONTACTS.thinus.phone.replace(/\s/g, '')}`} className="cta-secondary" data-testid="cta-phone-thinus">
              <Phone size={20} />
              <div className="cta-btn-text">
                <span className="cta-btn-name">{CONTACTS.thinus.name}</span>
                <span className="cta-btn-number">{CONTACTS.thinus.phone}</span>
              </div>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

// Sales Team Page
const SalesTeam = () => {
  const teamMembers = [
    {
      name: "Thinus Smit",
      role: "Sales Manager",
      number: "01",
      image: "https://customer-assets.emergentagent.com/job_motor-rewinding-team/artifacts/o0aica9a_Thinus%20Profile%20Photo.png",
      description: "As Sales Manager at JHWinders Enterprise, Thinus Smit plays a key leadership role in supporting both our clients and sales team. With extensive industry knowledge and a practical approach to problem-solving, he provides guidance and technical support to our sales representatives, assisting them in resolving challenges and ensuring every project is handled efficiently. Thinus works closely with clients across the power, water, cable, mining, and steel industries, building strong relationships while delivering reliable solutions tailored to their operational needs. His commitment to exceptional service, teamwork, and customer satisfaction makes him an invaluable part of the JHWinders team."
    },
    {
      name: "Annerie Kotze",
      role: "Sales Representative",
      number: "02",
      image: "https://customer-assets.emergentagent.com/job_motor-rewinding-team/artifacts/9q0s2doh_Annerie%20Profile%20Photo.png",
      description: "As a dedicated Sales Representative at JHWinders Enterprise, Annerie Kotze has built strong and lasting relationships with both existing and new clients through her professionalism, reliability, and commitment to exceptional customer service. She is dedicated to ensuring that any challenges or concerns are handled promptly and professionally, working closely with clients to provide effective solutions and a seamless service experience. Annerie manages clients across the water, cable, mining, and steel industries, where her excellent communication skills and customer-focused approach have earned the trust and confidence of those she serves."
    },
    {
      name: "Deline Brevis",
      role: "Sales Representative",
      number: "03",
      image: "https://customer-assets.emergentagent.com/job_motor-rewinding-team/artifacts/zospy9be_Delene%20Profile%20Photo.png",
      description: "As a Sales Representative at JHWinders Enterprise, Deline Brevis is committed to delivering outstanding customer satisfaction through professional, reliable, and client-focused service. Known for her approachable and compassionate nature, she builds positive relationships with clients while handling every challenge with professionalism and efficiency. Deline is dedicated to ensuring that each client receives the highest standard of service, creating confidence in the quality and reliability that JHWinders is known for. She currently manages clients within the steel industry, where she is focused on providing dependable support and maintaining long-term customer relationships."
    }
  ];

  return (
    <div className="sales-team-page" data-testid="sales-team-page">
      <div className="page-header">
        <span className="section-tag">JH Winders Enterprise</span>
        <h1>Our Sales Team</h1>
        <p>Real people, real expertise. Meet the team behind every successful repair — from first call to final dispatch.</p>
      </div>

      <section className="team-members-section">
        <div className="section-container">
          {teamMembers.map((member, index) => (
            <div key={index} className={`team-member ${index % 2 === 1 ? 'reverse' : ''}`} data-testid={`team-member-${index}`}>
              <div className="team-member-image">
                <img src={member.image} alt={member.name} />
              </div>
              <div className="team-member-content">
                <span className="team-member-number">{member.number} · {member.role}</span>
                <h2>{member.name}</h2>
                <p>{member.description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="full-team-section">
        <div className="section-container">
          <div className="section-header">
            <span className="section-tag">The full team</span>
            <h2>Sales Team</h2>
          </div>
          <div className="full-team-image">
            <img src="https://customer-assets-lqy194kg.emergentagent.net/job_motor-pro-refresh/artifacts/2z0lfnc7_Sales%20team.png" alt="JH Winders Enterprise sales team" />
          </div>
          <p className="full-team-description">
            The JHWinders Enterprise Sales Team is committed to delivering exceptional service, building long-term client relationships, and providing reliable solutions across a wide range of industries. Working closely with customers in the power, water, cable, mining, and steel sectors, our team combines industry knowledge, professionalism, and a customer-focused approach to ensure every client receives the highest standard of service. From identifying operational requirements to resolving challenges efficiently, our sales representatives work collaboratively with clients and internal departments to deliver responsive support, trusted advice, and solutions that keep businesses operating smoothly. At JHWinders Enterprise, customer satisfaction, integrity, and service excellence remain at the heart of everything our sales team does.
          </p>
        </div>
      </section>
    </div>
  );
};

// Workshop Overview Page
const Workshop = () => {
  const stages = [
    {
      number: "01",
      title: "Workshop Receiving Process",
      subtitle: "Inspection & assessment — where every quality repair begins.",
      slug: "receiving-process",
      image: "https://customer-assets.emergentagent.com/job_motor-rewinding-team/artifacts/7es0h9ie_1.%20Dismantle%20Station.jpg"
    },
    {
      number: "02",
      title: "Component Preparation",
      subtitle: "Service, assembly & testing — restoring performance through expert workmanship.",
      slug: "component-preparation",
      image: "https://customer-assets.emergentagent.com/job_motor-rewinding-team/artifacts/oc6777jt_3.%20Cleaners.png"
    },
    {
      number: "03",
      title: "Motor Rewinding Process",
      subtitle: "Rewinding excellence — precision rewinding for reliable performance.",
      slug: "motor-rewinding",
      image: "https://customer-assets.emergentagent.com/job_motor-rewinding-team/artifacts/1abbduc5_5.%20Coil%20Station.jpg"
    },
    {
      number: "04",
      title: "Engineering & Component Restoration",
      subtitle: "Restoring critical components with engineering excellence.",
      slug: "engineering-restoration",
      image: "https://customer-assets.emergentagent.com/job_motor-rewinding-team/artifacts/fdgw0fgk_14.%20Engineering.png"
    },
    {
      number: "05",
      title: "Finishing, Quality Control & Dispatch",
      subtitle: "The final touches that define quality.",
      slug: "finishing-dispatch",
      image: "https://customer-assets.emergentagent.com/job_motor-rewinding-team/artifacts/xbsg1u5a_11.%20Dispatch%20Area.jpg"
    }
  ];

  return (
    <div className="workshop-page" data-testid="workshop-page">
      <div className="page-header">
        <span className="section-tag">Our Workshop</span>
        <h1>Trusted Industrial Motor & Pump Specialists</h1>
      </div>

      <section className="workshop-intro">
        <div className="section-container">
          <div className="workshop-intro-content">
            <p>For more than 25 years, JHWinders Enterprise has proudly delivered reliable motor and pump solutions to industries throughout South Africa. Our commitment to quality workmanship, technical expertise and exceptional customer service has earned us the trust of businesses across mining, manufacturing, agriculture, municipalities and the industrial sector.</p>
            
            <p>Having successfully repaired and refurbished over 5,000 electric motors, we understand that every minute of downtime impacts productivity. That is why our highly skilled technicians work with precision, urgency and dedication to restore equipment efficiently while maintaining the highest standards of workmanship.</p>
            
            <p>Our comprehensive range of services includes electric motor repairs, professional motor rewinding, pump repairs, preventative maintenance, diagnostics and complete refurbishment solutions. Every project is carried out using proven repair techniques, quality materials and strict quality control procedures to ensure long-lasting performance and reliability.</p>
            
            <p>When unexpected failures occur, our 24-hour emergency breakdown support is ready to respond. Our experienced team works around the clock to minimise downtime, providing dependable assistance and fast turnaround times to help keep your operations running without unnecessary delays.</p>
            
            <p>At JHWinders Enterprise, we don't simply repair equipment — we build long-term partnerships through dependable service, honest workmanship and engineering solutions our customers can rely on. Whether it's a single motor repair or the complete refurbishment of critical rotating equipment, our goal remains the same: to deliver quality, reliability and performance that keeps your business moving forward.</p>
          </div>
        </div>
      </section>

      <section className="workshop-stages">
        <div className="section-container">
          <div className="section-header">
            <span className="section-tag">Explore the process</span>
            <h2>Five Stages. One Uncompromising Standard.</h2>
          </div>
          <div className="stages-grid">
            {stages.map((stage, index) => (
              <Link 
                key={index} 
                to={`/workshop/${stage.slug}`} 
                className="stage-card"
                data-testid={`stage-card-${index}`}
              >
                <div className="stage-image">
                  <img src={stage.image} alt={stage.title} />
                </div>
                <div className="stage-content">
                  <span className="stage-number">{stage.number}</span>
                  <h3>{stage.title}</h3>
                  <p>{stage.subtitle}</p>
                  <span className="read-more">Read more <ChevronRight size={16} /></span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

// Workshop Stage Detail Page
const WorkshopStageDetail = () => {
  const { stage } = require('react-router-dom').useParams();
  
  const stagesData = {
    "receiving-process": {
      tag: "Workshop Process",
      title: "Inspection & Assessment",
      subtitle: "Where Every Quality Repair Begins",
      images: [
        { url: "https://customer-assets.emergentagent.com/job_motor-rewinding-team/artifacts/7es0h9ie_1.%20Dismantle%20Station.jpg", label: "Dismantle Station", number: "01" },
        { url: "https://customer-assets.emergentagent.com/job_motor-rewinding-team/artifacts/5mu21rgt_8.%20Test%20Bay.jpg", label: "Test Bay", number: "02" },
        { url: "https://customer-assets.emergentagent.com/job_motor-rewinding-team/artifacts/qyqle4wx_2.%20Holding%20Bay.jpg", label: "Holding Area", number: "03" }
      ],
      content: [
        "Every repair at JHWinders Enterprise begins with a structured inspection and assessment process designed to ensure complete accuracy, traceability, and quality control from the moment equipment arrives at our workshop. Upon arrival, every electric motor is officially booked into our workshop management system, where it is assigned a unique job number and an individual job card. This documentation accompanies the motor throughout every stage of the repair process, allowing our technicians to maintain complete service records and monitor progress with precision.",
        "Before any technical work begins, each motor undergoes a thorough sandblasting process to remove dirt, grease, corrosion, and surface contaminants. This ensures that every component can be properly inspected while providing a clean surface for accurate assessment and future refurbishment.",
        "Once cleaned, the motor is transferred to our dismantling department, where experienced technicians carefully strip and disassemble the unit. Every component is inspected individually to identify wear, mechanical damage, electrical failures, and any additional issues that may affect the motor's performance. This detailed assessment enables our team to determine the most appropriate repair solution while ensuring that no faults are overlooked.",
        "Following the dismantling and inspection process, the motor is moved to our testing facility, where specialised testing procedures are carried out to determine whether the unit requires servicing, rewinding, refurbishment, or additional repairs. Various electrical and mechanical specifications are evaluated to ensure an accurate diagnosis before any repair work is authorised.",
        "Once the complete assessment has been finalised, the motor is carefully transferred to our designated holding area, where it remains safely stored until client approval or an official purchase order is received. This organised workflow allows JHWinders Enterprise to maintain efficiency, transparency, and complete control throughout every stage of the repair process, ensuring that each project progresses smoothly from inspection through to final completion."
      ],
      prev: null,
      next: { slug: "component-preparation", title: "Component Preparation" }
    },
    "component-preparation": {
      tag: "Workshop Process",
      title: "Service, Assembly & Testing",
      subtitle: "Restoring Performance Through Expert Workmanship",
      images: [
        { url: "https://customer-assets.emergentagent.com/job_motor-rewinding-team/artifacts/oc6777jt_3.%20Cleaners.png", label: "Cleaners", number: "01" },
        { url: "https://customer-assets.emergentagent.com/job_motor-rewinding-team/artifacts/nei52sym_7.%20Assemble%20Station.jpg", label: "Assemble Station", number: "02" },
        { url: "https://customer-assets.emergentagent.com/job_motor-rewinding-team/artifacts/ftupoe0n_8.%20Test%20Bay.jpg", label: "Test Bay", number: "03" }
      ],
      content: [
        "Once client approval or an official purchase order has been received, the motor officially enters the service and refurbishment phase of our workshop process. Every step is carefully managed to ensure the highest standards of quality, precision, and reliability before the motor is returned to service.",
        "The dismantled motor and its individual components are first transferred to our specialised cleaning department, where every reusable part is thoroughly cleaned, washed, and polished. Years of grease, oil, corrosion, and other contaminants are removed to ensure each component is in the best possible condition for inspection, repair, and reassembly. This process not only improves the quality of the finished repair but also allows hidden defects or excessive wear to be identified before work continues.",
        "Once the cleaning process has been completed, motors requiring a standard service are transferred to our assembly department. Here, our experienced technicians carefully assess every component to determine whether replacement parts are required. Where necessary, replacement components are sourced and ordered to ensure the motor is restored using quality parts that meet the required specifications.",
        "After all inspections have been completed and any required components have been received, the motor is professionally reassembled with careful attention to detail. Every component is fitted according to manufacturer specifications and workshop quality standards to ensure reliable operation and long-term performance.",
        "The fully assembled motor is then transferred to our testing facility, where comprehensive final inspections and performance testing are carried out. Electrical and mechanical tests are performed to verify that the motor meets all operational requirements before being approved for the next stage of the workshop process. This final quality assurance step provides our customers with the confidence that every motor leaving JHWinders Enterprise has been serviced, assembled, and tested to the highest industry standards."
      ],
      prev: { slug: "receiving-process", title: "Workshop Receiving Process" },
      next: { slug: "motor-rewinding", title: "Motor Rewinding Process" }
    },
    "motor-rewinding": {
      tag: "Workshop Process",
      title: "Rewinding Excellence",
      subtitle: "Precision Rewinding for Reliable Performance",
      images: [
        { url: "https://customer-assets.emergentagent.com/job_motor-rewinding-team/artifacts/1abbduc5_5.%20Coil%20Station.jpg", label: "Coil Station", number: "01" },
        { url: "https://customer-assets.emergentagent.com/job_motor-rewinding-team/artifacts/375q0jc6_6.%20Rewinding%20Sation.jpg", label: "Rewinding Station", number: "02" },
        { url: "https://customer-assets.emergentagent.com/job_motor-rewinding-team/artifacts/k5v7fsda_4.%20Baking%20oven%20%26%20Varnish%20Station.jpg", label: "Baking & Curing Oven with Varnish Station", number: "03" }
      ],
      content: [
        "Electric motor rewinding is one of the core services offered by JHWinders Enterprise and is carried out with precision, technical expertise, and strict quality control standards. Once the cleaning process has been completed and the motor has been assessed as requiring a rewind, it is transferred to our specialised rewinding department where the restoration process begins. Our experienced technicians carefully remove the damaged windings and prepare the stator before installing new windings using high-quality insulation materials and proven rewinding techniques that meet industry standards.",
        "After the rewinding process has been completed, the stator undergoes a professional varnishing process to strengthen the insulation system, improve durability, and provide long-term protection against moisture, vibration, and electrical stress. The varnished stator is then placed in one of our specialised baking ovens, where the curing process ensures maximum insulation integrity and long-lasting performance. Standard AC motors are baked for approximately 10 hours, while Slip Ring and DC motors require an extended baking period of 12 to 14 hours to achieve optimal curing and insulation performance.",
        "Once the baking cycle has been completed, the stator is transferred to our testing facility for a comprehensive Drop Test. This critical quality control procedure verifies the integrity of the newly installed windings and insulation system before the motor proceeds to the next stage of the refurbishment process. Conducting this test prior to assembly ensures that the rewind has been completed to the highest standards and allows any potential issues to be identified before final assembly begins.",
        "Following successful completion of the Drop Test, the motor is transferred to our assembly department, where experienced technicians carefully rebuild the unit using strict quality control procedures and manufacturer specifications. Once fully assembled, the motor returns to our testing facility for comprehensive final assessment and performance testing. A series of electrical and mechanical tests are performed to verify that the motor meets all required operational specifications before being approved for completion.",
        "This meticulous rewinding, testing, and assembly process ensures that every motor leaving JHWinders Enterprise delivers dependable performance, maximum efficiency, and long-term reliability, even in the most demanding industrial environments."
      ],
      prev: { slug: "component-preparation", title: "Component Preparation" },
      next: { slug: "engineering-restoration", title: "Engineering & Component Restoration" }
    },
    "engineering-restoration": {
      tag: "Workshop Process",
      title: "Restoring Critical Components with Engineering Excellence",
      subtitle: "Precision Engineering That Supports Long-Term Reliability",
      images: [
        { url: "https://customer-assets.emergentagent.com/job_motor-rewinding-team/artifacts/fdgw0fgk_14.%20Engineering.png", label: "Engineering Station", number: "01" },
        { url: "https://customer-assets.emergentagent.com/job_motor-rewinding-team/artifacts/xye33hdt_14.1.%20Engineering.webp", label: "Engineering Station", number: "02" }
      ],
      content: [
        "During the assembly process, every motor undergoes a detailed mechanical inspection to ensure that all components meet the required performance and quality standards. Our experienced technicians carefully assess each part for signs of wear, damage, or dimensional irregularities that could affect the reliability or efficiency of the completed motor. This thorough evaluation allows our team to identify any additional engineering work that may be required before the motor can continue through the refurbishment process.",
        "Whenever engineering repairs or modifications are necessary, the motor or its individual components are transferred to our specialised Engineering department. Here, our skilled technician carries out precision engineering work to restore worn or damaged components to their original specifications. Every repair is completed with meticulous attention to detail, ensuring that critical components meet the required tolerances and are fully prepared for reliable operation once reassembled.",
        "Our engineering department plays a vital role in extending the service life of industrial motors by restoring components that can safely and effectively be returned to service. This not only helps reduce unnecessary replacement costs but also ensures that each motor continues through the workshop process with components that meet our strict quality standards.",
        "Once all required engineering work has been completed, the restored components are returned to the assembly department, where the motor is professionally reassembled using our comprehensive quality control procedures. The completed unit is then transferred to our testing facility for final assessment and performance testing, ensuring that every engineering repair contributes to a motor that delivers dependable performance, operational efficiency, and long-term reliability.",
        "At JHWinders Enterprise, precision engineering is more than a support service — it is an essential part of our commitment to delivering high-quality workmanship and engineering solutions our customers can trust. Every component restored by our engineering team reflects the same attention to detail, technical expertise, and dedication to excellence that defines every stage of our workshop process."
      ],
      prev: { slug: "motor-rewinding", title: "Motor Rewinding Process" },
      next: { slug: "finishing-dispatch", title: "Finishing, Quality Control & Dispatch" }
    },
    "finishing-dispatch": {
      tag: "Workshop Process",
      title: "The Final Touches That Define Quality",
      subtitle: "Delivering Excellence Through Every Final Detail",
      images: [
        { url: "https://customer-assets.emergentagent.com/job_motor-rewinding-team/artifacts/m9o5cu00_10.%20Spray%20Bay.jpg", label: "Spray Bay", number: "01" },
        { url: "https://customer-assets.emergentagent.com/job_motor-rewinding-team/artifacts/5jp09o1t_12.%20QC%20Station.jpg", label: "QC Station", number: "02" },
        { url: "https://customer-assets.emergentagent.com/job_motor-rewinding-team/artifacts/xbsg1u5a_11.%20Dispatch%20Area.jpg", label: "Dispatch Area", number: "03" }
      ],
      content: [
        "Once all repair, engineering, rewinding, assembly, and testing processes have been successfully completed, the motor proceeds to our specialised spray painting department for its final finishing stage. Here, each motor is professionally prepared and spray painted according to the customer's specific colour requirements. Many of our long-standing clients have designated corporate colour specifications, ensuring consistency across their equipment and maintaining their operational standards.",
        "Our experienced spray painting technicians take great care to achieve a smooth, even, and professional finish, ensuring that every motor leaves the workshop with a high-quality appearance free from imperfections. Attention to detail during this stage not only enhances the overall presentation of the motor but also provides an additional layer of protection against corrosion and harsh operating environments.",
        "After the painting process has been completed, the motor is allowed to air dry naturally to ensure a durable and fully cured finish. Once the paint has cured, the motor is fitted with its identification plaque, containing all the relevant motor specifications and information required for future identification, maintenance, and traceability.",
        "The completed motor is then transferred to our dedicated Quality Control department, where it undergoes a comprehensive final inspection. Every aspect of the repair, assembly, testing, and finishing process is carefully verified to ensure the motor meets JHWinders Enterprise's strict quality standards before being approved for release.",
        "Once final quality approval has been granted, the motor is transferred to our Dispatch department, where it is carefully prepared for collection or delivery to the customer. Every motor leaves our workshop fully inspected, professionally finished, and ready to return to service, reflecting the quality, reliability, and workmanship that have defined JHWinders Enterprise for more than 25 years."
      ],
      prev: { slug: "engineering-restoration", title: "Engineering & Component Restoration" },
      next: null
    }
  };

  const stageInfo = stagesData[stage];

  if (!stageInfo) {
    return (
      <div className="service-detail-page">
        <div className="page-header">
          <h1>Page Not Found</h1>
          <Link to="/workshop" className="btn-primary" style={{ marginTop: '2rem' }}>Back to Workshop</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="workshop-stage-page" data-testid="workshop-stage-page">
      <div className="page-header">
        <span className="section-tag">{stageInfo.tag}</span>
        <h1>{stageInfo.title}</h1>
        <p>{stageInfo.subtitle}</p>
      </div>

      <section className="stage-images-section">
        <div className="section-container">
          <div className={`stage-images-grid stage-images-${stageInfo.images.length}`}>
            {stageInfo.images.map((img, index) => (
              <div key={index} className="stage-image-card">
                <img src={img.url} alt={img.label} />
                <div className="stage-image-caption">
                  <span className="caption-number">{img.number}</span>
                  <span className="caption-label">{img.label}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="stage-content-section">
        <div className="section-container">
          <div className="stage-content">
            {stageInfo.content.map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </div>
        </div>
      </section>

      <section className="stage-navigation">
        <div className="section-container">
          <div className="stage-nav-buttons">
            {stageInfo.prev ? (
              <Link to={`/workshop/${stageInfo.prev.slug}`} className="stage-nav-btn prev">
                <ChevronRight size={20} style={{ transform: 'rotate(180deg)' }} />
                <div>
                  <span className="nav-label">Previous</span>
                  <span className="nav-title">{stageInfo.prev.title}</span>
                </div>
              </Link>
            ) : <div></div>}
            
            {stageInfo.next ? (
              <Link to={`/workshop/${stageInfo.next.slug}`} className="stage-nav-btn next">
                <div>
                  <span className="nav-label">Next</span>
                  <span className="nav-title">{stageInfo.next.title}</span>
                </div>
                <ChevronRight size={20} />
              </Link>
            ) : (
              <Link to="/workshop" className="stage-nav-btn next">
                <div>
                  <span className="nav-label">Back to</span>
                  <span className="nav-title">Workshop Overview</span>
                </div>
                <ChevronRight size={20} />
              </Link>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

// About Page
const About = () => {
  const coreValues = [
    {
      icon: <Award size={40} />,
      title: "Quality Assurance",
      description: "ISO 9001:2015 certified processes ensuring consistent high-quality workmanship across all projects."
    },
    {
      icon: <Target size={40} />,
      title: "Customer Focus",
      description: "Dedicated to understanding and meeting our clients' unique requirements with precision."
    },
    {
      icon: <Star size={40} />,
      title: "Excellence",
      description: "Committed to delivering the highest standards of engineering excellence in every repair."
    },
    {
      icon: <Users size={40} />,
      title: "Expert Team",
      description: "Skilled technicians with decades of combined experience in motor and pump repairs."
    }
  ];

  const warrantyFeatures = [
    "Skilled workmanship guarantee",
    "Original specification restoration",
    "Free consultation on warranty claims",
    "Fast turnaround on warranty repairs"
  ];

  return (
    <div className="about-page" data-testid="about-page">
      {/* Hero Section */}
      <div className="about-hero">
        <div className="about-hero-content">
          <span className="section-tag">About Us</span>
          <h1>Precision Electromechanical Integrity Since 2017</h1>
          <p>
            JHWinders Enterprise (Pty) Ltd is a highly skilled engineering company focused on electric motors, 
            pumps, and electromechanical equipment. Based in Meyerton, Gauteng, we serve clients nationwide 
            with dedication to quality and value.
          </p>
        </div>
      </div>

      {/* Mission Section */}
      <section className="about-mission">
        <div className="section-container">
          <div className="mission-grid">
            <div className="mission-image">
              <img src={IMAGES.aboutWorkshop} alt="JHWinders Workshop" />
            </div>
            <div className="mission-content">
              <span className="section-tag">Our Mission</span>
              <h2>Delivering High Performance & Value-Driven Solutions</h2>
              <p>
                At JHWinders, we are committed to providing cost-effective, efficient, and reliable solutions 
                to clients across various industries. Our customer-centric approach ensures that we understand 
                and meet the unique needs of each project.
              </p>
              <p>
                We focus on improving equipment lifespan and productivity, helping our clients minimize downtime 
                and maximize their operational efficiency. Our ISO 9001:2015 certification reflects our commitment 
                to maintaining international standards in all our operations.
              </p>
              <div className="mission-highlight">
                <Zap size={24} />
                <div>
                  <h4>Our Commitment</h4>
                  <p>Excellence in every repair, precision in every rewind. We deliver solutions that extend equipment life and maximize operational uptime.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values Section */}
      <section className="core-values-section">
        <div className="section-container">
          <div className="section-header">
            <span className="section-tag">Core Values</span>
            <h2>What Drives Us</h2>
          </div>
          <div className="values-grid">
            {coreValues.map((value, index) => (
              <div key={index} className="value-card" data-testid={`value-card-${index}`}>
                <div className="value-icon">{value.icon}</div>
                <h3>{value.title}</h3>
                <p>{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Warranty Section */}
      <section className="warranty-section">
        <div className="section-container">
          <div className="warranty-grid">
            <div className="warranty-content">
              <span className="section-tag">Our Warranty</span>
              <h2>Quality You Can Trust</h2>
              <p>
                We stand behind our work with comprehensive warranty coverage, giving you peace of mind 
                and confidence in our repairs.
              </p>
              <ul className="warranty-features">
                {warrantyFeatures.map((feature, index) => (
                  <li key={index}>
                    <CheckCircle size={20} />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
            <div className="warranty-cards">
              <div className="warranty-card ac">
                <h3>AC Motors</h3>
                <div className="warranty-duration">12 Months</div>
                <p>Full warranty coverage on all AC motor repairs and rewinding services.</p>
              </div>
              <div className="warranty-card dc">
                <h3>DC Motors</h3>
                <div className="warranty-duration">6 Months</div>
                <p>Comprehensive warranty on DC motor repairs and refurbishment work.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ISO Certification */}
      <section className="iso-section">
        <div className="section-container">
          <div className="iso-content">
            <Award size={60} />
            <div>
              <h3>ISO 9001:2015</h3>
              <p>Quality Management System Certified</p>
              <span>Our certification ensures consistent quality in all our processes, from initial assessment to final delivery.</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Services Page
const Services = () => {
  const coreServices = [
    {
      title: "Electric Motor Repairs",
      description: "Complete repair solutions for AC and DC motors of all sizes, from fractional to large industrial motors.",
      image: IMAGES.serviceElectricMotor,
      features: ["High & Low Voltage Motors", "Slip Ring Motors", "Variable Speed Motors", "Servo Motors"],
    },
    {
      title: "Motor Rewinding",
      description: "Expert rewinding services restoring motors to original specifications with precision engineering.",
      image: IMAGES.serviceMotorRewinding,
      features: ["Full Rewind Services", "Partial Rewind", "Insulation Upgrade", "Winding Diagnostics"],
    },
    {
      title: "Refurbishment",
      description: "Complete motor and equipment refurbishment to extend lifespan and improve performance.",
      image: IMAGES.serviceRefurbishment,
      features: ["Bearing Replacement", "Shaft Repair", "Housing Machining", "Dynamic Balancing"],
    },
    {
      title: "Maintenance & Testing",
      description: "Comprehensive testing and preventive maintenance programs to minimize unexpected downtime.",
      image: IMAGES.serviceMaintenanceTesting,
      features: ["Insulation Testing", "Vibration Analysis", "Thermal Imaging", "Predictive Maintenance"],
    },
  ];

  const specializedServices = [
    { title: "Submersible Pumps", slug: "submersible-pumps", description: "Repair and maintenance of submersible pumps for water and wastewater applications." },
    { title: "Centrifugal Pumps", slug: "centrifugal-pumps", description: "Complete service solutions for centrifugal pumps of all sizes and configurations." },
    { title: "Diesel Generators", slug: "diesel-generators", description: "Generator maintenance, repair, and rewinding services for backup power systems." },
    { title: "Gearboxes", slug: "gearboxes", description: "Gearbox repair, reconditioning, and oil analysis services." },
    { title: "Electromagnets", slug: "electromagnets", description: "Repair and rewinding of industrial electromagnets and lifting magnets." },
    { title: "Welding Machines", slug: "welding-machines", description: "Service and repair of industrial welding equipment and transformers." },
    { title: "Winch Motors", slug: "winch-motors", description: "Specialized repair services for winch motors used in mining and industrial applications." },
    { title: "On-Site Services", slug: "on-site-services", description: "Mobile repair teams for on-site repairs and emergency breakdown services." },
  ];

  const technicalCapabilities = [
    "High Voltage Motor Testing (up to 11kV)",
    "Low Voltage Motor Diagnostics",
    "Pump Performance Testing",
    "Dynamic Balancing Equipment",
    "Thermal Imaging Systems",
    "Vibration Analysis Tools",
  ];

  return (
    <div className="services-page" data-testid="services-page">
      {/* Hero Section */}
      <div className="page-header">
        <span className="section-tag">Our Services</span>
        <h1>Comprehensive Engineering Solutions</h1>
        <p>From electric motor repairs to pump refurbishment, we offer a full range of engineering services to keep your operations running smoothly.</p>
      </div>

      {/* Core Services */}
      <section className="core-services-section">
        <div className="section-container">
          <div className="section-header">
            <span className="section-tag">Core Services</span>
            <h2>Primary Service Offerings</h2>
          </div>
          <div className="core-services-grid">
            {coreServices.map((service, index) => (
              <div key={index} className="core-service-card" data-testid={`core-service-${index}`}>
                <div className="core-service-image">
                  <img src={service.image} alt={service.title} />
                </div>
                <div className="core-service-content">
                  <h3>{service.title}</h3>
                  <p>{service.description}</p>
                  <ul className="service-features-list">
                    {service.features.map((feature, i) => (
                      <li key={i}>
                        <CheckCircle size={16} />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Link to="/contact" className="service-cta">
                    Get a Quote <ArrowRight size={18} />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Specialized Services */}
      <section className="specialized-services-section">
        <div className="section-container">
          <div className="section-header">
            <span className="section-tag">Specialized Services</span>
            <h2>Additional Capabilities</h2>
          </div>
          <div className="specialized-services-grid">
            {specializedServices.map((service, index) => (
              <Link 
                key={index} 
                to={`/services/${service.slug}`}
                className="specialized-service-card" 
                data-testid={`specialized-service-${index}`}
              >
                <h4>{service.title}</h4>
                <p>{service.description}</p>
                <span className="learn-more">Learn More <ChevronRight size={16} /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Technical Capabilities */}
      <section className="technical-section">
        <div className="section-container">
          <div className="technical-grid">
            <div className="technical-content">
              <span className="section-tag">Technical Capabilities</span>
              <h2>State-of-the-Art Equipment & Expertise</h2>
              <p>Our workshop is equipped with the latest diagnostic and repair equipment, enabling us to handle motors and pumps of all sizes and complexities.</p>
              <ul className="technical-list">
                {technicalCapabilities.map((cap, index) => (
                  <li key={index}>
                    <CheckCircle size={18} />
                    {cap}
                  </li>
                ))}
              </ul>
            </div>
            <div className="technical-cta-box">
              <h3>Need a Specific Service?</h3>
              <p>Don't see what you're looking for? Contact us to discuss your specific requirements. Our team can provide custom solutions for unique challenges.</p>
              <Link to="/contact" className="btn-primary">
                Contact Our Team <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Breakdown CTA */}
      <section className="services-breakdown-cta">
        <div className="section-container">
          <div className="breakdown-cta-content">
            <h2>24/7 Breakdown Services</h2>
            <p>Emergency assistance available around the clock. Fast response, nationwide coverage.</p>
            <Link to="/breakdown" className="btn-secondary">
              Breakdown Services <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

// Specialized Service Detail Page
const SpecializedServiceDetail = () => {
  const { slug } = require('react-router-dom').useParams();
  
  // User's workshop image
  const workshopImage = "https://customer-assets.emergentagent.com/job_motor-pro-refresh/artifacts/v4tfva7e_WhatsApp%20Image%202026-03-28%20at%2016.54.21.jpeg";
  
  const servicesData = {
    "submersible-pumps": {
      title: "Submersible Pumps",
      description: "Repair and maintenance of submersible pumps for water and wastewater applications.",
      longDescription: "Our team specializes in the complete repair and maintenance of submersible pumps used in various applications including water supply, wastewater treatment, irrigation, and industrial processes. We handle all major brands and sizes.",
      stockImage: "https://customer-assets.emergentagent.com/job_motor-pro-refresh/artifacts/o1wlfiao_Teal%20submersible%20pump%20in%20dramatic%20light.png",
      workshopImage: workshopImage,
      features: [
        "Complete pump overhaul and refurbishment",
        "Motor rewinding for submersible motors",
        "Mechanical seal replacement",
        "Impeller repair and replacement",
        "Cable and connector repairs",
        "Performance testing and certification"
      ],
      applications: ["Borehole pumps", "Sewage pumps", "Drainage pumps", "Mine dewatering", "Industrial water supply"]
    },
    "centrifugal-pumps": {
      title: "Centrifugal Pumps",
      description: "Complete service solutions for centrifugal pumps of all sizes and configurations.",
      longDescription: "We provide comprehensive repair and maintenance services for centrifugal pumps across all industries. From small process pumps to large industrial units, our expertise covers the full spectrum of centrifugal pump technology.",
      stockImage: "https://customer-assets.emergentagent.com/job_motor-pro-refresh/artifacts/6arci3nu_ChatGPT%20Image%20Apr%208%2C%202026%2C%2005_04_36%20PM.png",
      workshopImage: workshopImage,
      features: [
        "Impeller balancing and replacement",
        "Wear ring machining and replacement",
        "Shaft repair and manufacturing",
        "Bearing and seal replacement",
        "Casing repair and machining",
        "Performance curve testing"
      ],
      applications: ["Water treatment plants", "Chemical processing", "HVAC systems", "Agricultural irrigation", "Fire protection systems"]
    },
    "diesel-generators": {
      title: "Diesel Generators",
      description: "Generator maintenance, repair, and rewinding services for backup power systems.",
      longDescription: "Keep your backup power systems reliable with our comprehensive diesel generator services. We service and repair generators of all sizes, from small standby units to large industrial prime power systems.",
      stockImage: "https://customer-assets.emergentagent.com/job_motor-pro-refresh/artifacts/6ebte1mb_ChatGPT%20Image%20Apr%209%2C%202026%2C%2009_08_50%20AM.png",
      workshopImage: null,
      features: [
        "Alternator rewinding",
        "AVR repair and replacement",
        "Exciter repairs",
        "Bearing replacement",
        "Rotor balancing",
        "Load bank testing"
      ],
      applications: ["Standby power systems", "Prime power generation", "Mining operations", "Construction sites", "Healthcare facilities"]
    },
    "gearboxes": {
      title: "Gearboxes",
      description: "Gearbox repair, reconditioning, and oil analysis services.",
      longDescription: "Our gearbox services cover inspection, repair, and complete reconditioning of industrial gearboxes. We use advanced diagnostic techniques including oil analysis to identify problems before they cause failures.",
      stockImage: "https://customer-assets.emergentagent.com/job_motor-pro-refresh/artifacts/oe21j3yf_ChatGPT%20Image%20Apr%209%2C%202026%2C%2009_05_40%20AM.png",
      workshopImage: null,
      features: [
        "Complete gearbox overhaul",
        "Gear tooth repair and replacement",
        "Bearing replacement",
        "Shaft repair and machining",
        "Oil analysis and condition monitoring",
        "Alignment services"
      ],
      applications: ["Conveyor systems", "Mixers and agitators", "Crushers and mills", "Fans and blowers", "Pump drives"]
    },
    "electromagnets": {
      title: "Electromagnets",
      description: "Repair and rewinding of industrial electromagnets and lifting magnets.",
      longDescription: "We specialize in the repair and rewinding of industrial electromagnets used in material handling and separation applications. Our services restore your electromagnets to original specifications.",
      stockImage: "https://customer-assets.emergentagent.com/job_motor-pro-refresh/artifacts/5zoyj9dr_ChatGPT%20Image%20Apr%209%2C%202026%2C%2009_16_11%20AM.png",
      workshopImage: null,
      features: [
        "Coil rewinding",
        "Insulation upgrade",
        "Lead and terminal repair",
        "Housing repair",
        "Performance testing",
        "Custom modifications"
      ],
      applications: ["Scrap handling", "Steel mills", "Recycling facilities", "Mining operations", "Manufacturing plants"]
    },
    "welding-machines": {
      title: "Welding Machines",
      description: "Service and repair of industrial welding equipment and transformers.",
      longDescription: "Our welding machine repair services cover all types of industrial welding equipment including MIG, TIG, stick welders, and spot welding machines. We repair both the electrical and mechanical components.",
      stockImage: "https://customer-assets.emergentagent.com/job_motor-pro-refresh/artifacts/aqbjrbso_Portable%20generator%20on%20industrial%20cart.png",
      workshopImage: null,
      features: [
        "Transformer rewinding",
        "Rectifier repair",
        "Control system repairs",
        "Wire feed system service",
        "Cooling system repairs",
        "Calibration and testing"
      ],
      applications: ["Manufacturing plants", "Fabrication shops", "Automotive industry", "Construction", "Shipyards"]
    },
    "winch-motors": {
      title: "Winch Motors",
      description: "Specialized repair services for winch motors used in mining and industrial applications.",
      longDescription: "Winch motors operate in demanding conditions and require specialized expertise. Our team has extensive experience in repairing winch motors for mining, marine, and industrial applications.",
      stockImage: "https://customer-assets.emergentagent.com/job_motor-pro-refresh/artifacts/ca2uh2k5_ChatGPT%20Image%20Apr%209%2C%202026%2C%2009_32_01%20AM.png",
      workshopImage: null,
      features: [
        "Motor rewinding",
        "Brake system repairs",
        "Encoder and feedback repairs",
        "Bearing replacement",
        "Shaft repair",
        "Dynamic testing"
      ],
      applications: ["Mining hoists", "Marine winches", "Construction cranes", "Theatre rigging", "Industrial lifting"]
    },
    "on-site-services": {
      title: "On-Site Services",
      description: "Mobile repair teams for on-site repairs and emergency breakdown services.",
      longDescription: "When equipment can't be moved, our mobile repair teams come to you. We provide on-site diagnostics, repairs, and maintenance services to minimize downtime and keep your operations running.",
      stockImage: "https://customer-assets.emergentagent.com/job_motor-pro-refresh/artifacts/257sbxh6_ChatGPT%20Image%20Apr%209%2C%202026%2C%2009_38_06%20AM.png",
      workshopImage: null,
      features: [
        "Emergency breakdown response",
        "On-site motor testing",
        "Vibration analysis",
        "Thermal imaging",
        "Alignment services",
        "Condition monitoring"
      ],
      applications: ["Power stations", "Mining sites", "Manufacturing plants", "Water treatment facilities", "Remote locations"]
    }
  };

  const service = servicesData[slug];

  if (!service) {
    return (
      <div className="service-detail-page">
        <div className="page-header">
          <h1>Service Not Found</h1>
          <p>The requested service page could not be found.</p>
        </div>
        <div className="section-container" style={{ padding: '2rem', textAlign: 'center' }}>
          <Link to="/services" className="btn-primary">Back to Services</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="service-detail-page" data-testid="service-detail-page">
      <div className="page-header">
        <Link to="/services" className="back-link"><ChevronRight size={16} style={{ transform: 'rotate(180deg)' }} /> Back to Services</Link>
        <h1>{service.title}</h1>
        <p>{service.description}</p>
      </div>

      {/* Image Gallery */}
      <section className="service-images-section">
        <div className="section-container">
          <div className="service-images-grid">
            <div className="service-main-image">
              <img src={service.stockImage} alt={service.title} />
            </div>
            {service.workshopImage && (
              <div className="service-workshop-image">
                <img src={service.workshopImage} alt="Our Workshop" />
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="service-detail-content">
        <div className="section-container">
          <div className="service-detail-grid">
            <div className="service-detail-main">
              <h2>Overview</h2>
              <p>{service.longDescription}</p>
              
              <h3>Our {service.title} Services Include:</h3>
              <ul className="service-detail-features">
                {service.features.map((feature, index) => (
                  <li key={index}>
                    <CheckCircle size={18} />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="service-detail-sidebar">
              <div className="applications-box">
                <h4>Applications</h4>
                <ul>
                  {service.applications.map((app, index) => (
                    <li key={index}>{app}</li>
                  ))}
                </ul>
              </div>
              
              <div className="cta-box">
                <h4>Need {service.title} Service?</h4>
                <p>Contact us for a quote or to discuss your requirements.</p>
                <Link to="/contact" className="btn-primary">
                  Get a Quote <ArrowRight size={18} />
                </Link>
                <a href="tel:0820575452" className="phone-link">
                  <Phone size={18} />
                  082 057 5452
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Breakdown CTA */}
      <section className="services-breakdown-cta">
        <div className="section-container">
          <div className="breakdown-cta-content">
            <h2>24/7 Breakdown Services</h2>
            <p>Emergency assistance available around the clock. Fast response, nationwide coverage.</p>
            <Link to="/breakdown" className="btn-secondary">
              Breakdown Services <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

// Breakdown Service Page
const BreakdownService = () => {
  return (
    <div className="breakdown-page" data-testid="breakdown-page">
      <div className="breakdown-hero">
        <div className="breakdown-hero-content">
          <AlertTriangle size={48} />
          <h1>24/7 Emergency Breakdown Service</h1>
          <p>Motor failure can halt your entire operation. Our emergency response team is available around the clock to get you back up and running.</p>
          <div className="breakdown-contacts" data-testid="breakdown-contacts">
            <a href={`tel:${CONTACTS.daniel.phone.replace(/\s/g, '')}`} className="breakdown-phone" data-testid="breakdown-phone-daniel">
              <Phone size={24} />
              <div>
                <span className="name">{CONTACTS.daniel.name}</span>
                <span className="role">{CONTACTS.daniel.role}</span>
                <span className="number">{CONTACTS.daniel.phone}</span>
              </div>
            </a>
            <a href={`tel:${CONTACTS.thinus.phone.replace(/\s/g, '')}`} className="breakdown-phone" data-testid="breakdown-phone-thinus">
              <Phone size={24} />
              <div>
                <span className="name">{CONTACTS.thinus.name}</span>
                <span className="role">{CONTACTS.thinus.role}</span>
                <span className="number">{CONTACTS.thinus.phone}</span>
              </div>
            </a>
          </div>
        </div>
      </div>

      <div className="breakdown-features">
        <div className="section-container">
          <h2>What We Offer</h2>
          <div className="features-grid">
            <div className="feature-card">
              <Clock size={40} />
              <h3>Rapid Response</h3>
              <p>Our team responds quickly to minimize your downtime</p>
            </div>
            <div className="feature-card">
              <Wrench size={40} />
              <h3>On-Site Repairs</h3>
              <p>Many repairs can be completed at your location</p>
            </div>
            <div className="feature-card">
              <Settings size={40} />
              <h3>All Motor Types</h3>
              <p>We service all makes and sizes of electric motors</p>
            </div>
            <div className="feature-card">
              <Shield size={40} />
              <h3>Quality Parts</h3>
              <p>We use only quality replacement parts</p>
            </div>
          </div>
        </div>
      </div>

      <div className="breakdown-process">
        <div className="section-container">
          <h2>Our Emergency Process</h2>
          <div className="process-steps">
            <div className="process-step">
              <div className="step-number">1</div>
              <h3>Call Us</h3>
              <p>Contact our emergency line any time, day or night</p>
            </div>
            <div className="process-step">
              <div className="step-number">2</div>
              <h3>Assessment</h3>
              <p>We'll assess the situation and dispatch a technician</p>
            </div>
            <div className="process-step">
              <div className="step-number">3</div>
              <h3>Repair</h3>
              <p>Expert repairs on-site or at our workshop</p>
            </div>
            <div className="process-step">
              <div className="step-number">4</div>
              <h3>Back Online</h3>
              <p>Get your operations running smoothly again</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Contact Page
const Contact = () => {
  return (
    <div className="contact-page" data-testid="contact-page">
      <div className="page-header">
        <h1>Contact Us</h1>
        <p>Get in touch with our team for quotes, enquiries, or emergency support</p>
      </div>

      <div className="contact-grid">
        <div className="contact-info-section">
          <h2>Get In Touch</h2>
          
          <div className="contact-cards">
            <div className="contact-card" data-testid="contact-card-daniel">
              <div className="contact-card-header">
                <Users size={24} />
                <div>
                  <h3>{CONTACTS.daniel.name}</h3>
                  <span>{CONTACTS.daniel.role}</span>
                </div>
              </div>
              <a href={`tel:${CONTACTS.daniel.phone.replace(/\s/g, '')}`} className="contact-phone">
                <Phone size={20} />
                {CONTACTS.daniel.phone}
              </a>
            </div>

            <div className="contact-card" data-testid="contact-card-thinus">
              <div className="contact-card-header">
                <Users size={24} />
                <div>
                  <h3>{CONTACTS.thinus.name}</h3>
                  <span>{CONTACTS.thinus.role}</span>
                </div>
              </div>
              <a href={`tel:${CONTACTS.thinus.phone.replace(/\s/g, '')}`} className="contact-phone">
                <Phone size={20} />
                {CONTACTS.thinus.phone}
              </a>
            </div>
          </div>

          {/* Office Contact Info */}
          <div className="office-info">
            <h3>Office Details</h3>
            
            <div className="office-detail">
              <Phone size={20} />
              <div>
                <span className="detail-label">Telephone</span>
                <a href="tel:0163621339">016 362 1339</a>
              </div>
            </div>
            
            <div className="office-detail">
              <Mail size={20} />
              <div>
                <span className="detail-label">Email</span>
                <a href="mailto:reception@jhwinders.co.za">reception@jhwinders.co.za</a>
              </div>
            </div>
            
            <div className="office-detail">
              <MapPin size={20} />
              <div>
                <span className="detail-label">Address</span>
                <address>
                  4 Albert Wessels Street,<br />
                  Sybrand van Niekerk Park,<br />
                  Meyerton
                </address>
              </div>
            </div>
          </div>

          <div className="emergency-notice">
            <AlertTriangle size={24} />
            <div>
              <h4>Emergency Breakdown?</h4>
              <p>Call us immediately for 24/7 emergency support</p>
            </div>
          </div>
        </div>

        <div className="contact-form-section">
          <h2>Send Us a Message</h2>
          <form className="contact-form" data-testid="contact-form">
            <div className="form-group">
              <label htmlFor="name">Your Name</label>
              <input type="text" id="name" name="name" required data-testid="contact-name" />
            </div>
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input type="email" id="email" name="email" required data-testid="contact-email" />
            </div>
            <div className="form-group">
              <label htmlFor="phone">Phone Number</label>
              <input type="tel" id="phone" name="phone" data-testid="contact-phone-input" />
            </div>
            <div className="form-group">
              <label htmlFor="service">Service Required</label>
              <select id="service" name="service" data-testid="contact-service">
                <option value="">Select a service</option>
                <option value="motor-repair">Electric Motor Repair</option>
                <option value="rewinding">Motor Rewinding</option>
                <option value="pump-repair">Pump Repair</option>
                <option value="emergency">Emergency Breakdown</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="message">Message</label>
              <textarea id="message" name="message" rows="5" required data-testid="contact-message"></textarea>
            </div>
            <button type="submit" className="submit-btn" data-testid="contact-submit">
              Send Message <ArrowRight size={18} />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

// Main App Component
function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Header />
        <main>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/services" element={<Services />} />
            <Route path="/services/:slug" element={<SpecializedServiceDetail />} />
            <Route path="/workshop" element={<Workshop />} />
            <Route path="/workshop/:stage" element={<WorkshopStageDetail />} />
            <Route path="/sales-team" element={<SalesTeam />} />
            <Route path="/breakdown" element={<BreakdownService />} />
            <Route path="/contact" element={<Contact />} />
          </Routes>
        </main>
        <Footer />
        <FloatingButtons />
      </BrowserRouter>
    </div>
  );
}

export default App;
